import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { schoolEnrolYearAPI, schoolAPI } from '../../services/api';
import './SchoolEnrolYearManagement.css';

const SchoolEnrolYearManagement = () => {
  const { t } = useTranslation(['schoolenrolyear', 'common']);

  // Data state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal/Form state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [currentItem, setCurrentItem] = useState({
    id: 0,
    schoolCode: '',
    enrolYear: new Date().getFullYear(),
    status: 'ACTIVE'
  });

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ppdFilter, setPpdFilter] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Dropdowns state
  const [schools, setSchools] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  // Bulk operations state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSchools, setBulkSchools] = useState([]);
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  const [bulkStatus, setBulkStatus] = useState('ACTIVE');

  // Load data on mount and when filters change
  useEffect(() => {
    loadItems();
  }, [currentPage, pageSize, searchTerm, yearFilter, statusFilter, ppdFilter]);

  // Load dropdowns on mount
  useEffect(() => {
    loadSchools();
    loadAvailableYears();
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await schoolEnrolYearAPI.getSchoolEnrolYears(
        currentPage,
        pageSize,
        searchTerm || null,
        yearFilter || null,
        statusFilter || null,
        ppdFilter || null
      );

      if (response.success) {
        setItems(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
      } else {
        setError(response.message || t('common:error.loadFailed'));
      }
    } catch (err) {
      console.error('Load error:', err);
      setError(t('common:error.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadSchools = async () => {
    try {
      const response = await schoolAPI.getSchools();
      if (response.success) {
        setSchools(response.data || []);
      }
    } catch (err) {
      console.error('Load schools error:', err);
    }
  };

  const loadAvailableYears = async () => {
    try {
      const response = await schoolEnrolYearAPI.getAvailableYears();
      if (response.success) {
        setAvailableYears(response.data || []);
      }
    } catch (err) {
      console.error('Load available years error:', err);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleCreateItem = () => {
    setModalMode('create');
    setCurrentItem({
      id: 0,
      schoolCode: '',
      enrolYear: new Date().getFullYear(),
      status: 'ACTIVE'
    });
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setModalMode('edit');
    setCurrentItem({
      id: item.id,
      schoolCode: item.schoolCode || '',
      enrolYear: item.enrolYear,
      status: item.status || 'ACTIVE'
    });
    setShowModal(true);
  };

  const handleSaveItem = async () => {
    try {
      let response;

      if (modalMode === 'create') {
        response = await schoolEnrolYearAPI.createSchoolEnrolYear({
          schoolCode: currentItem.schoolCode,
          enrolYear: currentItem.enrolYear,
          status: currentItem.status
        });
      } else {
        response = await schoolEnrolYearAPI.updateSchoolEnrolYear(currentItem.id, {
          id: currentItem.id,
          schoolCode: currentItem.schoolCode,
          enrolYear: currentItem.enrolYear,
          status: currentItem.status
        });
      }

      if (response.success) {
        setShowModal(false);
        loadItems();
        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} enrollment year successfully`);
      } else {
        setError(response.message || t('common:error.saveFailed'));
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(t('common:error.saveFailed'));
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm(t('schoolenrolyear:confirmDelete'))) {
      try {
        const response = await schoolEnrolYearAPI.deleteSchoolEnrolYear(id);
        if (response.success) {
          loadItems();
          console.log('Enrollment year deleted successfully');
        } else {
          setError(response.message || t('common:error.deleteFailed'));
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError(t('common:error.deleteFailed'));
      }
    }
  };

  const handleBulkCreate = () => {
    setBulkSchools([]);
    setBulkYear(new Date().getFullYear());
    setBulkStatus('ACTIVE');
    setShowBulkModal(true);
  };

  const handleBulkCreateSubmit = async () => {
    if (bulkSchools.length === 0) {
      setError(t('schoolenrolyear:selectSchools'));
      return;
    }

    try {
      const response = await schoolEnrolYearAPI.bulkCreateSchoolEnrolYear({
        schoolCodes: bulkSchools,
        enrolYear: bulkYear,
        status: bulkStatus
      });

      if (response.success) {
        setShowBulkModal(false);
        loadItems();
        alert(t('schoolenrolyear:bulkCreateSuccess', {
          success: response.data.successCount,
          failed: response.data.failureCount
        }));
      } else {
        setError(response.message || t('common:error.saveFailed'));
      }
    } catch (err) {
      console.error('Bulk create error:', err);
      setError(t('common:error.saveFailed'));
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleReportIssueClick = () => {
    window.location.href = '/issue-reports';
  };

  return (
    <div className="master-data-management">
      {/* Header */}
      <div className="header-container">
        <img className="logo" src="/img/KTC-logo.png" alt="KTC" />
        <h2>{t('schoolenrolyear:title')}</h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick}>
          <i className="fas fa-bug"></i>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
          <button onClick={() => setError('')} className="close-btn">&times;</button>
        </div>
      )}

      {/* Toolbar */}
      <div className="master-data-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            placeholder={t('schoolenrolyear:searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="search-input"
          />

          <select
            value={yearFilter}
            onChange={(e) => {
              setYearFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">{t('schoolenrolyear:allYears')}</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">{t('schoolenrolyear:allStatuses')}</option>
            <option value="ACTIVE">{t('schoolenrolyear:active')}</option>
            <option value="INACTIVE">{t('schoolenrolyear:inactive')}</option>
          </select>

          <button onClick={handleSearch} className="btn-search">
            <i className="fas fa-search"></i> {t('common:search')}
          </button>
        </div>

        <div className="action-buttons">
          <button onClick={handleBulkCreate} className="btn-create">
            <i className="fas fa-plus-circle"></i> {t('schoolenrolyear:bulkAdd')}
          </button>
          <button onClick={handleCreateItem} className="btn-create">
            <i className="fas fa-plus"></i> {t('schoolenrolyear:addNew')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">{t('common:loading')}</div>
        ) : items.length === 0 ? (
          <div className="no-data">{t('common:noData')}</div>
        ) : (
          <table className="master-data-table">
            <thead>
              <tr>
                <th>{t('schoolenrolyear:schoolCode')}</th>
                <th>{t('schoolenrolyear:schoolName')}</th>
                <th>{t('schoolenrolyear:enrolYear')}</th>
                <th>{t('schoolenrolyear:status')}</th>
                <th>{t('schoolenrolyear:lastUpdated')}</th>
                <th>{t('schoolenrolyear:lastUpdatedBy')}</th>
                <th className="actions-column">{t('common:actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.schoolCode}</td>
                  <td>{item.schoolName}</td>
                  <td>{item.enrolYear}</td>
                  <td>
                    <span className={`badge ${item.status === 'ACTIVE' ? 'badge-success' : 'badge-secondary'}`}>
                      {item.status === 'ACTIVE' ? t('schoolenrolyear:active') : t('schoolenrolyear:inactive')}
                    </span>
                  </td>
                  <td>{item.lastUpdatedAt ? new Date(item.lastUpdatedAt).toLocaleDateString() : '-'}</td>
                  <td>{item.lastUpdatedBy || '-'}</td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditItem(item)}
                      className="btn-action btn-edit"
                      title={t('common:edit')}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="btn-action btn-delete"
                      title={t('common:delete')}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            {t('common:page')} {currentPage} {t('common:of')} {totalPages} ({totalCount} {t('common:items')})
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              {t('common:first')}
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              {t('common:previous')}
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => goToPage(Number(e.target.value))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>{t('common:of')} {totalPages}</span>
            </span>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              {t('common:next')}
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              {t('common:last')}
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 {t('common:perPage')}</option>
            <option value={50}>50 {t('common:perPage')}</option>
            <option value={100}>100 {t('common:perPage')}</option>
          </select>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === 'create'
                  ? t('schoolenrolyear:addNew')
                  : t('schoolenrolyear:editItem', { code: currentItem.schoolCode })}
              </h3>
              <button onClick={() => setShowModal(false)} className="modal-close-btn">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('schoolenrolyear:schoolCode')} *</label>
                <select
                  value={currentItem.schoolCode}
                  onChange={(e) => setCurrentItem({ ...currentItem, schoolCode: e.target.value })}
                  className="form-control"
                  disabled={modalMode === 'edit'}
                >
                  <option value="">{t('schoolenrolyear:selectSchool')}</option>
                  {schools.map((school) => (
                    <option key={school.schoolCode} value={school.schoolCode}>
                      {school.schoolCode} - {school.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('schoolenrolyear:enrolYear')} *</label>
                <input
                  type="number"
                  value={currentItem.enrolYear}
                  onChange={(e) => setCurrentItem({ ...currentItem, enrolYear: parseInt(e.target.value) })}
                  className="form-control"
                  min="2020"
                  max="2100"
                  disabled={modalMode === 'edit'}
                />
              </div>

              <div className="form-group">
                <label>{t('schoolenrolyear:status')} *</label>
                <select
                  value={currentItem.status}
                  onChange={(e) => setCurrentItem({ ...currentItem, status: e.target.value })}
                  className="form-control"
                >
                  <option value="ACTIVE">{t('schoolenrolyear:active')}</option>
                  <option value="INACTIVE">{t('schoolenrolyear:inactive')}</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowModal(false)} className="btn btn-secondary">
                {t('common:cancel')}
              </button>
              <button
                onClick={handleSaveItem}
                disabled={!currentItem.schoolCode || !currentItem.enrolYear}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? t('common:create') : t('common:update')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>{t('schoolenrolyear:bulkAdd')}</h3>
              <button onClick={() => setShowBulkModal(false)} className="modal-close-btn">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('schoolenrolyear:selectSchools')} *</label>
                <select
                  multiple
                  value={bulkSchools}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setBulkSchools(selected);
                  }}
                  className="form-control bulk-select"
                  size="10"
                >
                  {schools.map((school) => (
                    <option key={school.schoolCode} value={school.schoolCode}>
                      {school.schoolCode} - {school.name}
                    </option>
                  ))}
                </select>
                <div className="form-help">
                  {t('schoolenrolyear:bulkSelectHelp')}
                </div>
              </div>

              <div className="form-group">
                <label>{t('schoolenrolyear:enrolYear')} *</label>
                <input
                  type="number"
                  value={bulkYear}
                  onChange={(e) => setBulkYear(parseInt(e.target.value))}
                  className="form-control"
                  min="2020"
                  max="2100"
                />
              </div>

              <div className="form-group">
                <label>{t('schoolenrolyear:status')} *</label>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="form-control"
                >
                  <option value="ACTIVE">{t('schoolenrolyear:active')}</option>
                  <option value="INACTIVE">{t('schoolenrolyear:inactive')}</option>
                </select>
              </div>

              <div className="bulk-summary">
                <strong>{t('schoolenrolyear:selectedCount')}:</strong> {bulkSchools.length} {t('schoolenrolyear:schools')}
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBulkModal(false)} className="btn btn-secondary">
                {t('common:cancel')}
              </button>
              <button
                onClick={handleBulkCreateSubmit}
                disabled={bulkSchools.length === 0}
                className="btn btn-primary"
              >
                {t('schoolenrolyear:createBulk')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolEnrolYearManagement;
